/**
 * Team Assignment Manager
 *
 * Admin UI for managing supervisor-staff relationships.
 * Allows assigning staff to supervisors, transferring, and viewing team structure.
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useTeamAssignments, useRoleTerminology } from '@/hooks/useTeamManagement';
import { TeamAssignment, TeamAssignmentType, ASSIGNMENT_TYPE_LABELS } from '@/types/teamStructure';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Users,
  UserPlus,
  ArrowRightLeft,
  Trash2,
  ChevronDown,
  ChevronRight,
  Building,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

interface TeamAssignmentManagerProps {
  organizationId: number;
  organizationName: string;
}

interface OrgUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

export function TeamAssignmentManager({
  organizationId,
  organizationName,
}: TeamAssignmentManagerProps) {
  const { t, language } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const {
    overview,
    assignments,
    loading,
    error,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    transferStaff,
    refetch,
  } = useTeamAssignments(organizationId);
  const { getRoleName } = useRoleTerminology(organizationId);

  // Local state
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<number[]>([]);
  const [expandedSupervisors, setExpandedSupervisors] = useState<Set<number>>(new Set());

  // Form state
  const [assignForm, setAssignForm] = useState({
    supervisor_id: 0,
    staff_id: 0,
    assignment_type: 'direct' as TeamAssignmentType,
    notes: '',
  });

  const [transferForm, setTransferForm] = useState({
    from_supervisor_id: 0,
    to_supervisor_id: 0,
  });

  // Fetch organization users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await apiClient.get(`/api/cbos/${organizationId}/users`, {
          headers: { 'X-CBO-ID': organizationId.toString() },
        });
        setUsers(response.data || []);
      } catch (err) {
        console.warn('Failed to fetch users');
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [organizationId]);

  // Filter users by role
  // Note: promotora and care_coordinator are legacy roles that map to 'staff' system role
  // They are kept here for backward compatibility with existing user data
  const supervisors = users.filter((u) =>
    ['supervisor', 'program_manager', 'org_admin'].includes(u.role)
  );
  const staffMembers = users.filter((u) =>
    ['staff', 'promotora', 'care_coordinator', 'viewer'].includes(u.role)
  );

  // Get assigned staff IDs
  const assignedStaffIds = new Set(assignments.map((a) => a.staff_id));
  const unassignedStaff = staffMembers.filter((s) => !assignedStaffIds.has(s.id));

  // Group assignments by supervisor
  const assignmentsBySupervisor = assignments.reduce((acc, assignment) => {
    const supId = assignment.supervisor_id;
    if (!acc[supId]) {
      acc[supId] = [];
    }
    acc[supId].push(assignment);
    return acc;
  }, {} as Record<number, TeamAssignment[]>);

  const handleCreateAssignment = async () => {
    const result = await createAssignment({
      supervisor_id: assignForm.supervisor_id,
      staff_id: assignForm.staff_id,
      assignment_type: assignForm.assignment_type,
      notes: assignForm.notes || undefined,
    });

    if (result) {
      setShowAssignDialog(false);
      setAssignForm({
        supervisor_id: 0,
        staff_id: 0,
        assignment_type: 'direct',
        notes: '',
      });
    }
  };

  const handleTransfer = async () => {
    if (selectedStaff.length === 0) return;

    const success = await transferStaff(
      selectedStaff,
      transferForm.from_supervisor_id,
      transferForm.to_supervisor_id
    );

    if (success) {
      setShowTransferDialog(false);
      setSelectedStaff([]);
      setTransferForm({ from_supervisor_id: 0, to_supervisor_id: 0 });
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    if (confirm(t('admin.confirm_remove_assignment', 'Remove this team assignment?'))) {
      await deleteAssignment(assignmentId);
    }
  };

  const toggleSupervisor = (supervisorId: number) => {
    setExpandedSupervisors((prev) => {
      const next = new Set(prev);
      if (next.has(supervisorId)) {
        next.delete(supervisorId);
      } else {
        next.add(supervisorId);
      }
      return next;
    });
  };

  const getAssignmentTypeBadge = (type: TeamAssignmentType) => {
    const labels = ASSIGNMENT_TYPE_LABELS[type];
    const label = language === 'es' ? labels.es : labels.en;

    const variants: Record<TeamAssignmentType, string> = {
      direct: 'bg-green-100 text-green-800',
      temporary: 'bg-yellow-100 text-yellow-800',
      backup: 'bg-blue-100 text-blue-800',
    };

    return (
      <Badge className={variants[type]} variant="outline">
        {label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{staffMembers.length}</p>
                <p className="text-sm text-muted-foreground">
                  {t('admin.total_staff', 'Total Staff')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{supervisors.length}</p>
                <p className="text-sm text-muted-foreground">
                  {t('admin.supervisors', 'Supervisors')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{assignments.length}</p>
                <p className="text-sm text-muted-foreground">
                  {t('admin.assignments', 'Assignments')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unassignedStaff.length}</p>
                <p className="text-sm text-muted-foreground">
                  {t('admin.unassigned', 'Unassigned')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('admin.team_structure', 'Team Structure')}</CardTitle>
              <CardDescription>
                {t('admin.team_structure_desc', 'Manage supervisor-staff assignments')}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowTransferDialog(true)}
                disabled={selectedStaff.length === 0}
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {t('admin.transfer', 'Transfer')} ({selectedStaff.length})
              </Button>
              <Button onClick={() => setShowAssignDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                {t('admin.assign_staff', 'Assign Staff')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="by-supervisor">
            <TabsList>
              <TabsTrigger value="by-supervisor">
                {t('admin.by_supervisor', 'By Supervisor')}
              </TabsTrigger>
              <TabsTrigger value="unassigned">
                {t('admin.unassigned_staff', 'Unassigned Staff')} ({unassignedStaff.length})
              </TabsTrigger>
              <TabsTrigger value="all">
                {t('admin.all_assignments', 'All Assignments')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="by-supervisor" className="mt-4">
              <div className="space-y-3">
                {supervisors.map((supervisor) => {
                  const teamAssignments = assignmentsBySupervisor[supervisor.id] || [];
                  const isExpanded = expandedSupervisors.has(supervisor.id);

                  return (
                    <div key={supervisor.id} className="border rounded-lg">
                      <button
                        onClick={() => toggleSupervisor(supervisor.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <div className="text-left">
                            <p className="font-medium">{supervisor.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {getRoleName(supervisor.role as any, language as 'en' | 'es')} • {supervisor.email}
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary">
                          {teamAssignments.length} {t('admin.team_members', 'team members')}
                        </Badge>
                      </button>

                      {isExpanded && (
                        <div className="border-t p-4 bg-muted/20">
                          {teamAssignments.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              {t('admin.no_team_members', 'No team members assigned')}
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-10">
                                    <input
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedStaff((prev) => [
                                            ...prev,
                                            ...teamAssignments.map((a) => a.staff_id),
                                          ]);
                                        } else {
                                          const ids = teamAssignments.map((a) => a.staff_id);
                                          setSelectedStaff((prev) =>
                                            prev.filter((id) => !ids.includes(id))
                                          );
                                        }
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead>{t('admin.staff_member', 'Staff Member')}</TableHead>
                                  <TableHead>{t('admin.role', 'Role')}</TableHead>
                                  <TableHead>{t('admin.type', 'Type')}</TableHead>
                                  <TableHead>{t('admin.since', 'Since')}</TableHead>
                                  <TableHead className="w-10"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {teamAssignments.map((assignment) => (
                                  <TableRow key={assignment.id}>
                                    <TableCell>
                                      <input
                                        type="checkbox"
                                        checked={selectedStaff.includes(assignment.staff_id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedStaff((prev) => [...prev, assignment.staff_id]);
                                          } else {
                                            setSelectedStaff((prev) =>
                                              prev.filter((id) => id !== assignment.staff_id)
                                            );
                                          }
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <p className="font-medium">{assignment.staff_name}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {assignment.staff_email}
                                        </p>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {assignment.staff_role &&
                                        getRoleName(assignment.staff_role as any, language as 'en' | 'es')}
                                    </TableCell>
                                    <TableCell>
                                      {getAssignmentTypeBadge(assignment.assignment_type)}
                                    </TableCell>
                                    <TableCell>
                                      {fmt.short(assignment.effective_from)}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteAssignment(assignment.id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {supervisors.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('admin.no_supervisors', 'No supervisors found. Users with supervisor, program_manager, or org_admin roles can manage teams.')}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="unassigned" className="mt-4">
              {unassignedStaff.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t('admin.all_staff_assigned', 'All staff members are assigned to supervisors.')}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.name', 'Name')}</TableHead>
                      <TableHead>{t('admin.email', 'Email')}</TableHead>
                      <TableHead>{t('admin.role', 'Role')}</TableHead>
                      <TableHead className="text-right">{t('admin.action', 'Action')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unassignedStaff.map((staff) => (
                      <TableRow key={staff.id}>
                        <TableCell className="font-medium">{staff.name}</TableCell>
                        <TableCell>{staff.email}</TableCell>
                        <TableCell>
                          {getRoleName(staff.role as any, language as 'en' | 'es')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => {
                              setAssignForm((prev) => ({
                                ...prev,
                                staff_id: staff.id,
                              }));
                              setShowAssignDialog(true);
                            }}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            {t('admin.assign', 'Assign')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="all" className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.staff', 'Staff')}</TableHead>
                    <TableHead>{t('admin.supervisor', 'Supervisor')}</TableHead>
                    <TableHead>{t('admin.type', 'Type')}</TableHead>
                    <TableHead>{t('admin.effective_from', 'Effective From')}</TableHead>
                    <TableHead>{t('admin.effective_to', 'Effective To')}</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{assignment.staff_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {assignment.staff_role}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{assignment.supervisor_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getAssignmentTypeBadge(assignment.assignment_type)}
                      </TableCell>
                      <TableCell>
                        {fmt.short(assignment.effective_from)}
                      </TableCell>
                      <TableCell>
                        {assignment.effective_to
                          ? fmt.short(assignment.effective_to)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAssignment(assignment.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.assign_staff_to_supervisor', 'Assign Staff to Supervisor')}</DialogTitle>
            <DialogDescription>
              {t('admin.assign_staff_desc', 'Select a supervisor and staff member to create a team assignment.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>{t('admin.supervisor', 'Supervisor')}</Label>
              <Select
                value={assignForm.supervisor_id.toString()}
                onValueChange={(v) =>
                  setAssignForm((prev) => ({ ...prev, supervisor_id: parseInt(v) }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.select_supervisor', 'Select supervisor')} />
                </SelectTrigger>
                <SelectContent>
                  {supervisors.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id.toString()}>
                      {sup.name} ({getRoleName(sup.role as any, language as 'en' | 'es')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('admin.staff_member', 'Staff Member')}</Label>
              <Select
                value={assignForm.staff_id.toString()}
                onValueChange={(v) =>
                  setAssignForm((prev) => ({ ...prev, staff_id: parseInt(v) }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.select_staff', 'Select staff member')} />
                </SelectTrigger>
                <SelectContent>
                  {unassignedStaff.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id.toString()}>
                      {staff.name} ({getRoleName(staff.role as any, language as 'en' | 'es')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('admin.assignment_type', 'Assignment Type')}</Label>
              <Select
                value={assignForm.assignment_type}
                onValueChange={(v) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    assignment_type: v as TeamAssignmentType,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">
                    {ASSIGNMENT_TYPE_LABELS.direct[language as 'en' | 'es']}
                  </SelectItem>
                  <SelectItem value="temporary">
                    {ASSIGNMENT_TYPE_LABELS.temporary[language as 'en' | 'es']}
                  </SelectItem>
                  <SelectItem value="backup">
                    {ASSIGNMENT_TYPE_LABELS.backup[language as 'en' | 'es']}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('admin.notes', 'Notes')} ({t('common.optional', 'Optional')})</Label>
              <Input
                value={assignForm.notes}
                onChange={(e) =>
                  setAssignForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={t('admin.assignment_notes_placeholder', 'e.g., Covering for vacation')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleCreateAssignment}
              disabled={!assignForm.supervisor_id || !assignForm.staff_id}
            >
              {t('admin.create_assignment', 'Create Assignment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.transfer_staff', 'Transfer Staff')}</DialogTitle>
            <DialogDescription>
              {t('admin.transfer_staff_desc', 'Move selected staff members to a different supervisor.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>{t('admin.selected_staff', 'Selected Staff')}</Label>
              <div className="p-3 bg-muted rounded-lg">
                {selectedStaff.length} {t('admin.staff_selected', 'staff member(s) selected')}
              </div>
            </div>

            <div>
              <Label>{t('admin.transfer_to', 'Transfer To')}</Label>
              <Select
                value={transferForm.to_supervisor_id.toString()}
                onValueChange={(v) =>
                  setTransferForm((prev) => ({ ...prev, to_supervisor_id: parseInt(v) }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.select_new_supervisor', 'Select new supervisor')} />
                </SelectTrigger>
                <SelectContent>
                  {supervisors.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id.toString()}>
                      {sup.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={!transferForm.to_supervisor_id}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              {t('admin.transfer', 'Transfer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TeamAssignmentManager;
